/**
 * The capture queue — IndexedDB, because a capture session must survive a
 * dropped network.
 *
 * A collector photographing 200 cards at a show will not accept losing work to
 * a flaky hotspot, so nothing is uploaded from memory. Every finished item is
 * written to IndexedDB first (image Blobs included), and only then does the
 * uploader try the network. Close the tab mid-session, run out of signal, walk
 * out of range: the queue is still there next time the screen opens.
 *
 * The SKU is minted at ENQUEUE time, not at upload time. That is what makes a
 * replay safe — the server upserts on `card://<sku>`, so retrying a half-sent
 * item updates the same card instead of creating a second one.
 */

const DB_NAME = 'smagent-capture';
const DB_VERSION = 1;
const STORE = 'queue';

export type QueueStatus = 'pending' | 'uploading' | 'done' | 'error';

export interface QueuedImage {
  role: 'front' | 'back' | 'other';
  blob: Blob;
  width: number;
  height: number;
  /** Set once the bytes are known to be in blob storage. */
  assetId?: string;
  /** Hex digest of the original bytes; also the dedup key server-side. */
  sha256?: string;
}

export interface QueuedItem {
  id: string;
  sku: string;
  accountId: string;
  createdAt: number;
  status: QueueStatus;
  attempts: number;
  lastError?: string;
  images: QueuedImage[];
  /** Card template fields, e.g. { player_name, year, card_number }. */
  fields: Record<string, unknown>;
  taxonomies: Record<string, string[]>;
  title?: string;
  ocrText?: string;
  publishStatus: string;
  /** Calibration profile active at capture time, stamped onto every asset. */
  captureMeta: Record<string, unknown> | null;
}

let dbPromise: Promise<IDBDatabase> | null = null;

function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const store = db.createObjectStore(STORE, { keyPath: 'id' });
        store.createIndex('byAccount', 'accountId');
        store.createIndex('byCreatedAt', 'createdAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('IndexedDB unavailable'));
  });
  return dbPromise;
}

function tx<T>(mode: IDBTransactionMode, fn: (store: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return openDb().then(
    (db) =>
      new Promise<T>((resolve, reject) => {
        const t = db.transaction(STORE, mode);
        const req = fn(t.objectStore(STORE));
        req.onsuccess = () => resolve(req.result);
        req.onerror = () => reject(req.error ?? new Error('IndexedDB request failed'));
      })
  );
}

/** Mint an identity that satisfies the server's SKU rule: [A-Za-z0-9._-]{1,64}. */
export function mintSku(prefix = 'cap') {
  const stamp = Date.now().toString(36);
  const rand = Math.random().toString(36).slice(2, 8);
  return `${prefix}-${stamp}-${rand}`;
}

export async function enqueue(
  item: Omit<QueuedItem, 'id' | 'createdAt' | 'status' | 'attempts'>
): Promise<QueuedItem> {
  const record: QueuedItem = {
    ...item,
    id: `${item.sku}`, // one queue row per SKU; a re-save replaces it in place
    createdAt: Date.now(),
    status: 'pending',
    attempts: 0,
  };
  await tx('readwrite', (s) => s.put(record) as unknown as IDBRequest<IDBValidKey>);
  return record;
}

export async function listQueue(accountId: string): Promise<QueuedItem[]> {
  const all = await tx<QueuedItem[]>('readonly', (s) => s.getAll() as IDBRequest<QueuedItem[]>);
  return all.filter((r) => r.accountId === accountId).sort((a, b) => a.createdAt - b.createdAt);
}

export async function updateItem(item: QueuedItem): Promise<void> {
  await tx('readwrite', (s) => s.put(item) as unknown as IDBRequest<IDBValidKey>);
}

export async function removeItem(id: string): Promise<void> {
  await tx('readwrite', (s) => s.delete(id) as unknown as IDBRequest<undefined>);
}

/**
 * Are we storing more than the browser will tolerate? Full-frame JPEGs run
 * 2–5 MB each, so 200 queued cards is comfortably a gigabyte — worth warning
 * about before the quota error arrives as a failed capture.
 */
export async function estimateUsage(): Promise<{ usage: number; quota: number } | null> {
  if (!navigator.storage?.estimate) return null;
  const { usage = 0, quota = 0 } = await navigator.storage.estimate();
  return { usage, quota };
}
